import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { useSubscription } from '../lib/useSubscription'
import { openCheckout, openManagement, paddleConfig } from '../lib/paddle'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'

export default function SubscriptionCard() {
  const { t } = useTranslation()
  const sub = useSubscription()
  const [opening, setOpening] = useState(false)

  const handleUpgrade = async () => {
    setOpening(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        alert(t('subscription.alert_signin_required'))
        setOpening(false)
        return
      }

      await openCheckout({
        userEmail: user.email,
        userId: user.id,
        onSuccess: () => {
          // Webhook will update profile shortly. Reload after a brief
          // delay so the UI reflects the new state.
          setTimeout(() => sub.reload(), 2500)
        },
        onClose: () => {
          setOpening(false)
        },
      })
    } catch (err) {
      console.error('Failed to open checkout:', err)
      alert(t('subscription.alert_checkout_failed'))
      setOpening(false)
    }
  }

  if (sub.loading) {
    return <div style={styles.loading}>{t('subscription.loading')}</div>
  }

  // Compute display state
  let statusBadge, statusBadgeColor, headline, sub_text, ctaLabel, ctaAction

  if (sub.lifetime) {
    statusBadge = t('subscription.badge_lifetime')
    statusBadgeColor = teal
    headline = t('subscription.lifetime_headline')
    sub_text = t('subscription.lifetime_sub')
    ctaLabel = null
  } else if (sub.paidActive && sub.status === 'active') {
    statusBadge = t('subscription.badge_active')
    statusBadgeColor = teal
    headline = `${paddleConfig.productName} ${paddleConfig.priceLabel}`
    sub_text = sub.currentPeriodEnd
      ? t('subscription.active_renews', { date: formatDate(sub.currentPeriodEnd) })
      : t('subscription.active_default')
    ctaLabel = t('subscription.cta_manage')
    ctaAction = openManagement
  } else if (sub.status === 'past_due') {
    statusBadge = t('subscription.badge_past_due')
    statusBadgeColor = '#DC2626'
    headline = t('subscription.past_due_headline')
    sub_text = t('subscription.past_due_sub')
    ctaLabel = t('subscription.cta_update_payment')
    ctaAction = handleUpgrade
  } else if (sub.status === 'canceled' && sub.currentPeriodEnd && sub.currentPeriodEnd > new Date()) {
    statusBadge = t('subscription.badge_canceling')
    statusBadgeColor = '#F59E0B'
    headline = t('subscription.canceling_headline')
    sub_text = t('subscription.canceling_sub', { date: formatDate(sub.currentPeriodEnd) })
    ctaLabel = t('subscription.cta_resubscribe')
    ctaAction = handleUpgrade
  } else if (sub.trialActive) {
    statusBadge = t('subscription.badge_trial')
    statusBadgeColor = teal
    headline = t('subscription.trial_headline', { count: sub.daysRemaining })
    sub_text = sub.trialEndsAt
      ? t('subscription.trial_sub_ends', { date: formatDate(sub.trialEndsAt) })
      : t('subscription.trial_sub_default')
    ctaLabel = t('subscription.cta_upgrade', { price: paddleConfig.priceLabel })
    ctaAction = handleUpgrade
  } else {
    // expired, canceled-and-period-ended, or unknown
    statusBadge = t('subscription.badge_expired')
    statusBadgeColor = '#DC2626'
    headline = t('subscription.expired_headline')
    sub_text = t('subscription.expired_sub')
    ctaLabel = t('subscription.cta_subscribe', { price: paddleConfig.priceLabel })
    ctaAction = handleUpgrade
  }

  return (
    <div style={styles.card}>
      <div style={styles.row}>
        <span style={{ ...styles.badge, background: `${statusBadgeColor}20`, color: statusBadgeColor }}>
          {statusBadge}
        </span>
      </div>

      <h3 style={styles.headline}>{headline}</h3>
      <p style={styles.sub}>{sub_text}</p>

      {ctaLabel && (
        <button
          style={{ ...styles.cta, opacity: opening ? 0.5 : 1 }}
          onClick={ctaAction}
          disabled={opening}
        >
          {opening ? t('subscription.opening_checkout') : ctaLabel}
        </button>
      )}

      <p style={styles.footnote}>
        {t('subscription.billing_help')}{' '}
        <a href="mailto:support@merqlog.com" style={styles.link}>
          support@merqlog.com
        </a>
      </p>
    </div>
  )
}

// Locale-aware date formatting. Falls back to en-US.
function formatDate(date) {
  if (!date) return ''
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const styles = {
  loading: {
    padding: '20px',
    color: '#9CA3AF',
    fontSize: '14px',
    textAlign: 'center',
  },
  card: {
    padding: '4px 0',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  badge: {
    fontSize: '11px',
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  headline: {
    fontFamily: "'Fraunces', serif",
    fontSize: '20px',
    fontWeight: 800,
    color: plum,
    letterSpacing: '-0.3px',
    margin: '0 0 6px 0',
    lineHeight: 1.25,
  },
  sub: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
    lineHeight: 1.5,
    margin: '0 0 20px 0',
  },
  cta: {
    width: '100%',
    padding: '14px 18px',
    background: teal,
    color: '#042f2e',
    border: 'none',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 6px 18px ${teal}40`,
    marginBottom: '12px',
  },
  footnote: {
    fontSize: '12px',
    color: '#9CA3AF',
    margin: 0,
    fontWeight: 500,
  },
  link: {
    color: teal,
    fontWeight: 700,
    textDecoration: 'none',
  },
}
